import { FormEvent, useState } from 'react';
import { Loader2, X, CheckCircle2 } from 'lucide-react';
import { useMutation } from '@tanstack/react-query';
import { registerNodes, type Platform } from '@/api/fleet';

export function RegisterNodesModal({
  onClose,
  onSuccess
}: {
  onClose: () => void;
  onSuccess: () => void;
}) {
  const [count, setCount] = useState(1);
  const [group, setGroup] = useState('default');
  const [platform, setPlatform] = useState<Platform>('linux');
  const [labelPrefix, setLabelPrefix] = useState('');
  const [result, setResult] = useState<{
    registered: number;
    nodeKeys: string[];
  } | null>(null);

  const mut = useMutation({
    mutationFn: () =>
      registerNodes({ count, group, platform, labelPrefix: labelPrefix || undefined }),
    onSuccess: setResult
  });

  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (count < 1 || count > 500) return;
    mut.mutate();
  };

  return (
    <div
      className="fixed inset-0 z-40 flex items-center justify-center bg-black/70 backdrop-blur-sm"
      onClick={e => e.target === e.currentTarget && onClose()}
    >
      <div className="w-full max-w-md rounded-2xl border border-emerald-500/30 bg-nurev-surface p-6">
        <div className="mb-4 flex items-center justify-between">
          <h2 className="text-lg font-bold text-nurev-text">
            {result ? 'Nodes registered' : 'Register new nodes'}
          </h2>
          <button onClick={onClose} className="text-nurev-muted hover:text-nurev-text">
            <X className="h-5 w-5" />
          </button>
        </div>

        {result ? (
          <div>
            <div className="mb-4 flex items-center gap-2 rounded-lg border border-emerald-500/40 bg-emerald-500/10 px-4 py-3">
              <CheckCircle2 className="h-5 w-5 text-emerald-300" />
              <span className="text-sm text-emerald-300">
                Registered {result.registered} node{result.registered === 1 ? '' : 's'}
              </span>
            </div>
            <p className="mb-2 text-xs font-semibold uppercase tracking-wider text-nurev-muted">
              Node keys (shown ONCE — copy now)
            </p>
            <pre className="max-h-48 overflow-auto rounded-lg border border-nurev-border bg-nurev-bg p-3 font-mono text-[11px] text-nurev-text">
              {result.nodeKeys.join('\n')}
            </pre>
            <button onClick={onSuccess} className="btn-primary mt-4 w-full">
              Done
            </button>
          </div>
        ) : (
          <form onSubmit={submit} className="space-y-4">
            <div>
              <label className="label">How many nodes</label>
              <input
                type="number"
                min={1}
                max={500}
                value={count}
                onChange={e => setCount(parseInt(e.target.value, 10) || 1)}
                className="input"
              />
              <p className="mt-1 text-xs text-nurev-muted">
                Max 500 per batch. Each node gets its own bearer-token key.
              </p>
            </div>
            <div>
              <label className="label">Group</label>
              <input
                type="text"
                value={group}
                onChange={e => setGroup(e.target.value)}
                placeholder="us-east"
                className="input"
              />
            </div>
            <div>
              <label className="label">Platform</label>
              <select
                value={platform}
                onChange={e => setPlatform(e.target.value as Platform)}
                className="input"
              >
                <option value="linux">Linux</option>
                <option value="windows">Windows</option>
                <option value="mac">macOS</option>
              </select>
            </div>
            <div>
              <label className="label">Label prefix (optional)</label>
              <input
                type="text"
                value={labelPrefix}
                onChange={e => setLabelPrefix(e.target.value)}
                placeholder="prod-"
                className="input"
              />
            </div>
            {mut.error && (
              <p className="rounded-lg border border-red-500/40 bg-red-500/10 px-3 py-2 text-xs text-red-300">
                {(mut.error as Error).message}
              </p>
            )}
            <button
              type="submit"
              disabled={mut.isPending}
              className="btn-primary w-full"
            >
              {mut.isPending ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                `Register ${count} node${count === 1 ? '' : 's'}`
              )}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
